import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { Account, NetworkType } from '@mls-wallet/shared-types';
import { createNetworkId, NETWORK_OPTIONS } from '@mls-wallet/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Section,
  SectionDescription,
  SectionLeftColumn,
  SectionRightColumn,
  SectionRow,
  SectionTitle,
} from '@/components/shared/SectionComponents';
import {
  type DRPhase,
  useDisasterRecoverySettings,
} from '@/hooks/admin/organization/use-disaster-recovery-settings';
import { type DisasterRecoveryProposalStatus } from '@mls-wallet/shared-types';
import { useAccounts } from '@/hooks/account/useAccounts';
import { useCreateAccountNetworkProposal } from '@/hooks/admin/use-create-account-network-proposal';
import { type AccountProposal } from '@/hooks/account/use-account-proposals';

interface AddAccountNetworksDialogProps {
  open: boolean;
  account: Account | null;
  onOpenChange: (open: boolean) => void;
  onProposalCreated: (proposal: AccountProposal) => void;
}

type DRNetworkRestrictionArgs = {
  drPhase: DRPhase | null;
  hasInitiationProposal: boolean;
  hasFinalizationProposal: boolean;
  finalizationProposalStatus: DisasterRecoveryProposalStatus | undefined;
};

function shouldRestrictNetworksDuringDRSetup({
  drPhase,
  hasInitiationProposal,
  hasFinalizationProposal,
  finalizationProposalStatus,
}: DRNetworkRestrictionArgs): boolean {
  // During DR setup, new undeployed networks are blocked because the org needs
  // DR fully configured before we deploy fresh account contracts there.
  const isInitiationCreated = hasInitiationProposal && drPhase === null;
  const isInitiationInTimelock = drPhase === 'TIMELOCK_WAIT';
  const isFinalizationApprovedAndExecuted =
    finalizationProposalStatus === 'APPROVED' && drPhase === 'COMPLETED';
  const hasBlockingFinalizationProposal =
    hasFinalizationProposal && !isFinalizationApprovedAndExecuted;

  return (
    isInitiationCreated ||
    isInitiationInTimelock ||
    hasBlockingFinalizationProposal
  );
}

export function AddAccountNetworksDialog({
  open,
  account,
  onOpenChange,
  onProposalCreated,
}: AddAccountNetworksDialogProps) {
  const [selectedNetworks, setSelectedNetworks] = useState<NetworkType[]>([]);
  const [pendingAddNetwork, setPendingAddNetwork] = useState<NetworkType | ''>(
    ''
  );
  const { accounts } = useAccounts();
  const { drConfig, drInitiationProposal, drFinalizationProposal } =
    useDisasterRecoverySettings();
  const { createNetworkProposal, isCreating, createError, clearCreateError } =
    useCreateAccountNetworkProposal();

  const accountNetworkIds = useMemo(
    () => new Set(account?.networks ?? []),
    [account]
  );
  const organizationNetworkIds = useMemo(
    () =>
      new Set(accounts.flatMap((currentAccount) => currentAccount.networks)),
    [accounts]
  );
  const finalizationProposal = drFinalizationProposal?.proposal;
  const shouldRestrictNetworkSelection = shouldRestrictNetworksDuringDRSetup({
    drPhase: drConfig?.phase ?? null,
    hasInitiationProposal: Boolean(drInitiationProposal?.proposal),
    hasFinalizationProposal: Boolean(finalizationProposal),
    finalizationProposalStatus: finalizationProposal?.status,
  });

  const availableNetworkOptions = useMemo(() => {
    // The dialog only offers networks missing from this specific account. The
    // server repeats this validation so bypassing the UI cannot create duplicates.
    const missingAccountNetworks = NETWORK_OPTIONS.filter(
      (network) => !accountNetworkIds.has(network.id)
    );

    // If DR setup is in progress, limit additions to networks where the org is
    // already deployed. This mirrors the new-account dialog's safety rule.
    return shouldRestrictNetworkSelection
      ? missingAccountNetworks.filter((network) =>
          organizationNetworkIds.has(network.id)
        )
      : missingAccountNetworks;
  }, [
    accountNetworkIds,
    organizationNetworkIds,
    shouldRestrictNetworkSelection,
  ]);
  const availableNetworkIds = useMemo(
    () => new Set(availableNetworkOptions.map((network) => network.id)),
    [availableNetworkOptions]
  );

  useEffect(() => {
    if (!open) {
      setSelectedNetworks([]);
      setPendingAddNetwork('');
      clearCreateError();
    }
  }, [clearCreateError, open]);

  useEffect(() => {
    // If DR state or account data changes while the dialog is open, drop any
    // selections that are no longer allowed.
    setSelectedNetworks((currentNetworks) =>
      currentNetworks.filter((networkId) => availableNetworkIds.has(networkId))
    );
  }, [availableNetworkIds]);

  const addNetwork = (networkId: NetworkType) => {
    if (!availableNetworkIds.has(networkId)) {
      return;
    }

    setSelectedNetworks((currentNetworks) =>
      currentNetworks.includes(networkId)
        ? currentNetworks
        : [...currentNetworks, networkId]
    );
    clearCreateError();
  };

  const removeNetwork = (networkId: NetworkType) => {
    setSelectedNetworks((currentNetworks) =>
      currentNetworks.filter((n) => n !== networkId)
    );
    clearCreateError();
  };

  const handleSubmit = async () => {
    if (!account || selectedNetworks.length === 0) return;

    try {
      const proposal = await createNetworkProposal({
        accountId: account.id,
        networkIds: selectedNetworks,
      });
      onProposalCreated(proposal);
      onOpenChange(false);
      setSelectedNetworks([]);
    } catch (e) {
      console.error('Failed to create account network proposal', e);
      // Error is already handled by the hook and stored in createError.
    }
  };

  if (!account) return null;

  const outlineColor = 'border-slate-300 border';

  return (
    <NestedFullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      noPadding
      title="Add networks"
      footer={
        <div className="flex justify-center gap-2 max-w-xl w-full px-6">
          <Button
            size="lg"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            onClick={handleSubmit}
            disabled={isCreating || selectedNetworks.length === 0}
          >
            {isCreating ? 'Creating...' : 'Create Proposal'}
          </Button>
        </div>
      }
      bodyContainerClassName="bg-muted"
    >
      <div className="p-6 max-w-3xl mx-auto w-full flex flex-col gap-8">
        <Section title="Blockchain Networks">
          <SectionRow>
            <SectionLeftColumn>
              <SectionTitle>{account.name}</SectionTitle>
              <SectionDescription>
                Select additional blockchain networks for this account
              </SectionDescription>
            </SectionLeftColumn>
            <SectionRightColumn>
              <div className="space-y-3">
                <Select
                  value={pendingAddNetwork ? String(pendingAddNetwork) : ''}
                  disabled={availableNetworkOptions.length === 0}
                  onValueChange={(v) => {
                    const id = createNetworkId(Number(v));
                    addNetwork(id);
                    setPendingAddNetwork('');
                  }}
                >
                  <SelectTrigger size="lg" className={`${outlineColor}`}>
                    <SelectValue placeholder="Choose blockchain networks" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableNetworkOptions.map((opt) => (
                      <SelectItem key={opt.id} value={String(opt.id)}>
                        {opt.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableNetworkOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No additional networks are available.
                  </p>
                ) : null}
                {shouldRestrictNetworkSelection ? (
                  <p className="text-xs text-orange-600">
                    Some networks are hidden until{' '}
                    <Link
                      to="/settings"
                      className="text-orange-600 underline hover:opacity-90"
                    >
                      Disaster Recovery
                    </Link>{' '}
                    is set up
                  </p>
                ) : null}
                {createError ? (
                  <p className="text-sm text-destructive" role="alert">
                    {createError}
                  </p>
                ) : null}

                {selectedNetworks.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedNetworks.map((nid) => {
                      const opt = NETWORK_OPTIONS.find((o) => o.id === nid);
                      return (
                        <Badge
                          key={nid}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          <span>{opt?.name ?? nid}</span>
                          <span
                            className="ml-1 inline-flex items-center justify-center cursor-pointer hover:text-destructive"
                            onClick={() => removeNetwork(nid)}
                          >
                            x
                          </span>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            </SectionRightColumn>
          </SectionRow>
        </Section>
      </div>
    </NestedFullscreenDialog>
  );
}
